<script lang="ts">
  import { onMount } from 'svelte';
  import { push } from 'svelte-spa-router';
  import Button from '../../Button.svelte';
  import Table from '../../Table.svelte';
  import Pagination from '../../Pagination.svelte';
  import Dropdown from '../../Dropdown.svelte';
  import SearchInput from '../../SearchInput.svelte';
  import adminEmployeesService, { type Employee, type AdminEmployeesFilters } from '../../../lib/api/admin/employees.service';
  import employersSearchService from '../../../lib/api/admin/employers-search.service';
  import userStatusesResourceService, { type UserStatus } from '../../../lib/api/resource/user-statuses.service';
  import AsyncSearchableDropdown from '../../AsyncSearchableDropdown.svelte';
  import toast from 'svelte-french-toast';
  import LoadingSkeleton from '../../LoadingSkeleton.svelte';
  import EmptyState from '../../EmptyState.svelte';
  import ErrorState from '../../ErrorState.svelte';
  import { createPreferencesManager } from '../../../lib/utils/preferences';

  // Preferences manager
  const preferences = createPreferencesManager('employee-management');

  // State
  let isLoading = true;
  let error = '';
  let employees: Employee[] = [];
  let totalPages = 1;
  let totalItems = 0;

  // Metrics/Statistics
  let totalEmployees = 0;
  let activeEmployees = 0;
  let pendingEmployees = 0;
  let totalDonations = 0;

  // Filters - initialize from preferences
  let searchPhrase = preferences.getFilters().searchPhrase || '';
  let selectedEmployer = preferences.getFilters().selectedEmployer || ''; // Empty string for "All Employers"
  let selectedStatus = preferences.getFilters().selectedStatus || ''; // Empty string for "All Statuses"
  
  // Pagination - initialize from preferences
  let currentPage = preferences.getCurrentPage() || 1;
  let itemsPerPage = preferences.getPageSize() || 15;

  // Employer popup state
  let showEmployerPopup = false;
  let popupPosition = { x: 0, y: 0 };
  let selectedEmployeeEmployers: any[] = [];

  // User statuses for filter dropdown
  let userStatuses: UserStatus[] = [];
  let statusOptions: Array<{ uuid: string; name: string }> = [];

  const tableColumns = [
    {
      key: "full_name",
      label: "Employee Name",
      width: "auto",
      render: (value: string) => `<span class="employee-name">${value}</span>`
    },
    {
      key: "email",
      label: "Email",
      width: "auto",
      render: (value: string) => `<span class="email">${value}</span>`
    },
    {
      key: "employers",
      label: "Employer",
      width: "auto",
      render: (value: any[], row: any) => {
        if (!value || value.length === 0) {
          return '<span class="no-employer">No employer</span>';
        }
        
        // Show only the first employer in the column
        const firstEmployer = value[0].employer_name;
        const hasMultipleEmployers = value.length > 1;
        
        // Show count only if there are multiple employers
        const countHtml = hasMultipleEmployers ? `<button class="employer-count" onclick="handleEmployerInfoClick(event, '${row.uuid}')" onmousedown="event.stopPropagation()">+${value.length - 1}</button>` : '';
        
        return `<div class="employer-cell">
          <span class="employer-name">${firstEmployer}</span>
          ${hasMultipleEmployers ? `<span class="employer-count-wrapper">${countHtml}</span>` : ''}
        </div>`;
      }
    },
    {
      key: "total_donation_amount",
      label: "Total Donated",
      width: "auto",
      render: (value: number) => `$${value.toLocaleString()}`
    },
    {
      key: "total_donated_hours",
      label: "Hours",
      width: "100px",
      render: (value: number) => `${value} hrs`
    },
    {
      key: "user_status",
      label: "Status",
      width: "144px",
      align: 'center',
      render: (value: any) => {
        if (!value || !value.name) return '<span class="no-status">-</span>';
        const statusName = value.name;
        const statusClass = statusName.toLowerCase().replace(/\s+/g, '-');
        return `<span class="status-badge status-${statusClass}">${statusName}</span>`;
      }
    },
    {
      key: "email_verified_at",
      label: "Join Date",
      width: "140px",
      render: (value: string) => {
        if (!value) return '<span class="no-date">Not verified</span>';
        try {
          const date = new Date(value);
          const day = date.getDate();
          const month = date.toLocaleDateString('en-US', { month: 'short' });
          const year = date.getFullYear();
          return `<span class="joining-date">${month} ${day}, ${year}</span>`;
        } catch {
          return '<span class="no-date">Invalid date</span>';
        }
      }
    }
  ];

  onMount(() => {
    loadEmployees();
    loadUserStatuses();
    setupGlobalHandlers();
  });

  function setupGlobalHandlers() {
    if (typeof window !== 'undefined') {
      (window as any).handleEmployerInfoClick = handleEmployerInfoClick;
    }
  }

  async function loadEmployees() {
    try {
      isLoading = true;
      error = '';
      
      const filters: AdminEmployeesFilters = {
        page: currentPage,
        per_page: itemsPerPage
      };
      
      if (searchPhrase) filters.phrase = searchPhrase;
      if (selectedEmployer && selectedEmployer.trim() !== '') filters.employer_uuid = selectedEmployer;
      if (selectedStatus && selectedStatus.trim() !== '') filters.user_status_uuid = selectedStatus;
      
      const response = await adminEmployeesService.getEmployees(filters);
      
      if (response.success && response.data) {
        employees = response.data;
        totalItems = response.paging?.total || 0;
        totalPages = response.paging?.last_page || 1;
        
        // Calculate metrics from the current page data
        // Note: For accurate metrics, we'd need a separate API call or aggregate data
        // For now, we'll calculate from current page and show totalItems
        totalEmployees = totalItems;
        activeEmployees = employees.filter(emp => emp.user_status?.name === 'Active').length;
        pendingEmployees = employees.filter(emp => emp.user_status?.name === 'Pending').length;
        totalDonations = employees.reduce((sum, emp) => sum + (emp.total_donation_amount || 0), 0);
      } else {
        error = response.message || 'Failed to load employees';
        toast.error(error);
        employees = [];
        totalEmployees = 0;
        activeEmployees = 0;
        pendingEmployees = 0;
        totalDonations = 0;
      }
    } catch (err) {
      error = 'Failed to load employees';
      toast.error(error);
      console.error('Error loading employees:', err);
      employees = [];
    } finally {
      isLoading = false;
    }
  }

  async function searchEmployers(query: string): Promise<Array<{uuid: string, name: string}>> {
    try {
      // If query is empty, return empty array (don't load all employers)
      if (!query.trim()) {
        return [];
      }
      
      // Search with limit of 10 results
      const results = await employersSearchService.searchEmployers(query, 10);
      return results;
    } catch (err) {
      console.error('Error searching employers:', err);
      return [];
    }
  }

  async function getEmployerLabel(uuid: string): Promise<string | null> {
    if (!uuid) return null;
    try {
      return await employersSearchService.getEmployerName(uuid);
    } catch (err) {
      console.error('Error getting employer label:', err);
      return null;
    }
  }

  async function loadUserStatuses() {
    try {
      const response = await userStatusesResourceService.getUserStatuses();
      
      if (response.success && response.data?.user_statuses) {
        const allStatuses = response.data.user_statuses;
        // Filter to show only the most relevant statuses: Pending, Active (approved), Declined (rejected)
        const relevantStatuses = allStatuses.filter((status: UserStatus) => 
          ['Pending', 'Active', 'Declined'].includes(status.name)
        );
        statusOptions = [
          { uuid: '', name: 'All Statuses' },
          ...relevantStatuses.map((status: UserStatus) => ({
            uuid: status.uuid,
            name: status.name
          }))
        ];
      } else {
        console.error('Failed to load user statuses: Invalid response structure');
        statusOptions = [{ uuid: '', name: 'All Statuses' }];
      }
    } catch (err) {
      console.error('Error loading user statuses:', err);
      statusOptions = [{ uuid: '', name: 'All Statuses' }];
    }
  }

  function handleSearch() {
    currentPage = 1;
    preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedStatus } });
    loadEmployees();
  }
  
  // Watch for filter changes and save preferences
  $: if (searchPhrase !== undefined || selectedEmployer !== undefined || selectedStatus !== undefined) {
    preferences.updateFilters({ searchPhrase, selectedEmployer, selectedStatus });
  }
  
  // Watch for page size changes
  $: if (itemsPerPage !== undefined) {
    preferences.update({ pageSize: itemsPerPage });
  }
  
  // Watch for page changes
  $: if (currentPage !== undefined) {
    preferences.update({ currentPage });
  }

  function handlePageChange(event: CustomEvent<{ page: number }>) {
    currentPage = event.detail.page;
    preferences.update({ currentPage });
    loadEmployees();
  }

  function handleEmployerInfoClick(event: Event, employeeUuid: string) {
    event.preventDefault();
    event.stopPropagation();
    
    const employee = employees.find(emp => emp.uuid === employeeUuid);
    if (employee && employee.employers) {
      selectedEmployeeEmployers = employee.employers;
      
      const button = event.target as HTMLElement;
      const rect = button.getBoundingClientRect();
      
      popupPosition = {
        x: rect.left,
        y: rect.bottom + 5
      };
      
      showEmployerPopup = true;
    }
  }


  function handleRowClick(employeeUuid: string) {
    // Navigate to employee profile
    push(`/admin/employee-profile/${employeeUuid}`);
  }

  function closeEmployerPopup() {
    showEmployerPopup = false;
  }

  // Close popup when clicking outside
  function handleClickOutside(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.employer-popup') && !target.closest('.employer-info-btn')) {
      closeEmployerPopup();
    }
  }
</script>

<svelte:window on:click={handleClickOutside} />

<div class="manage-list-page">
  <div class="page-header">
    <p class="subtitle">Manage and monitor all employees across the platform</p>
  </div>

  <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-label">Total Employees</div>
        <div class="metric-value">{totalEmployees.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Active</div>
        <div class="metric-value">{activeEmployees.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Pending</div>
        <div class="metric-value">{pendingEmployees.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Total Donations</div>
        <div class="metric-value">${totalDonations.toLocaleString()}</div>
      </div>
  </div>

  <div class="filters-section">
    <div class="filters-left">
      <div class="filter-item">
        <SearchInput
          bind:value={searchPhrase}
          placeholder="Search employees"
          on:search={handleSearch}
          width="250px"
        />
      </div>
      
      <div class="filter-item">
        <AsyncSearchableDropdown
          placeholder="Search employer..."
          selectedValue={selectedEmployer}
          searchFunction={searchEmployers}
          getSelectedLabel={getEmployerLabel}
          onSelectionChange={(value) => {
            selectedEmployer = value;
            currentPage = 1;
            preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedStatus } });
            handleSearch();
          }}
          debounceMs={300}
        />
      </div>
      
      <div class="filter-item">
        <Dropdown
          options={statusOptions}
          selectedValue={selectedStatus}
          placeholder="All Statuses"
          onSelectionChange={(value) => {
            selectedStatus = value;
            currentPage = 1;
            preferences.update({ currentPage: 1, filters: { searchPhrase, selectedEmployer, selectedStatus } });
            handleSearch();
          }}
        />
      </div>
    </div>
    
    <div class="filters-right">
      <Button variant="primary" on:click={handleSearch}>
        Search
      </Button>
    </div>
  </div>

  <!-- Employees Table Section -->
  <div class="table-section">
  {#if error}
      <ErrorState 
        message="Failed to load employees" 
        description={error}
        onRetry={loadEmployees}
      />
    {:else if isLoading}
      <div class="loading-wrap">
        <LoadingSkeleton lines={10} height="2.5rem" />
    </div>
    {:else if !employees || employees.length === 0}
      <EmptyState 
        message="No employees found"
        description={searchPhrase || selectedEmployer || selectedStatus ? "Try adjusting your filters to see more results." : "Get started by adding your first employee."}
        actionLabel="Reset Filters"
        onAction={() => { searchPhrase = ''; selectedEmployer = ''; selectedStatus = ''; handleSearch(); }}
      />
    {:else}
      <Table
        columns={tableColumns}
        data={employees}
        onRowClick={(row) => handleRowClick(row.uuid)}
      />

    {#if totalPages > 1}
        <div class="pagination-container">
        <Pagination
          {currentPage}
          {totalPages}
          on:pageChange={handlePageChange}
        />
      </div>
    {/if}
  {/if}
  </div>

  <!-- Employer Info Popup -->
  {#if showEmployerPopup}
    <div 
      class="employer-popup"
      style="left: {popupPosition.x}px; top: {popupPosition.y}px;"
    >
      <div class="popup-header">
        <h3>Employer Information</h3>
        <button class="close-btn" on:click={closeEmployerPopup}>
          <svg viewBox="0 0 20 20" width="16" height="16" fill="currentColor">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
      
      <div class="popup-content">
        {#each selectedEmployeeEmployers as employer}
          <div class="employer-item">
            <div class="employer-name">{employer.employer_name}</div>
            <div class="employer-stats">
              <div class="stat-item">
                <span class="stat-label">Total Donated:</span>
                <span class="stat-value">${employer.total_donation_amount.toLocaleString()} ({employer.total_hours} hrs)</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Pending:</span>
                <span class="stat-value">({employer.total_pending_hours} hrs)</span>
              </div>
            </div>
          </div>
        {/each}
      </div>
    </div>
  {/if}
</div>

<style>
  .page-header {
    margin-bottom: 1.5rem;
  }

  .subtitle {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .metric-card {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 1rem 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .metric-label {
    font-size: 0.875rem;
    color: #6b7280;
    font-weight: 500;
    margin: 0;
  }

  .metric-value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    width: 100%;
  }

  .filters-left {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
    flex: 1;
    flex-wrap: wrap;
    width: 100%;
  }

  .filter-item {
    flex: 1 1 auto;
    min-width: 140px;
    max-width: 300px;
  }

  .filter-item :global(.async-dropdown-container),
  .filter-item :global(.search-input-wrapper),
  .filter-item :global(.dropdown-wrapper) {
    width: 100%;
  }

  .filters-right {
    flex-shrink: 0;
    display: flex;
    gap: 0.75rem;
    align-items: center;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    overflow: hidden;
  }

  .loading-wrap {
    padding: 2rem;
    min-height: 320px;
  }

  .pagination-container {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f3f4f6;
  }

  /* Table Styling */
  :global(.employee-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.email) {
    color: #6b7280;
    font-size: 0.875rem;
  }

  :global(.no-employer) {
    color: #9ca3af;
    font-style: italic;
  }

  :global(.joining-date) {
    color: #111827;
    font-size: 0.875rem;
  }

  :global(.no-date) {
    color: #9ca3af;
    font-style: italic;
    font-size: 0.875rem;
  }

  :global(.employer-cell) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  :global(.employer-name) {
    font-weight: 500;
    color: #111827;
  }

  :global(.employer-info-btn) {
    background: none;
    border: none;
    padding: 0.25rem;
    border-radius: 4px;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
  }

  :global(.employer-info-btn:hover) {
    background: #f3f4f6;
    color: #374151;
  }

  /* Employer Popup */
  .employer-popup {
    position: fixed;
    background: white;
    border-radius: 8px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    border: 1px solid #e5e7eb;
    min-width: 300px;
    max-width: 400px;
    z-index: 1000;
  }

  .popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }

  .popup-header h3 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #111827;
  }

  .close-btn {
    background: none;
    border: none;
    padding: 0.25rem;
    border-radius: 4px;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s;
  }

  .close-btn:hover {
    background: #f3f4f6;
    color: #374151;
  }

  .popup-content {
    padding: 1rem;
  }

  .employer-item {
    padding: 0.75rem 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .employer-item:last-child {
    border-bottom: none;
  }

  .employer-item .employer-name {
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  .employer-stats {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .stat-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .stat-label {
    font-size: 0.875rem;
    color: #6b7280;
  }

  .stat-value {
    font-size: 0.875rem;
    font-weight: 500;
    color: #111827;
  }

  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  :global(.status-active) {
    background-color: #d1fae5;
    color: #065f46;
  }

  :global(.status-pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-declined) {
    background-color: #fee2e2;
    color: #991b1b;
  }

  :global(.status-requested) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  :global(.no-status) {
    color: #9ca3af;
    font-style: italic;
  }

  :global(.employer-cell) {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    max-width: 100%;
    flex-wrap: nowrap;
    text-align: left;
    justify-content: flex-start;
  }

  :global(.employer-name) {
    display: inline-block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 0.875rem;
    color: #111827;
    font-weight: 500;
    flex-shrink: 1;
    min-width: 0;
    text-align: left;
  }

  :global(.employer-count-wrapper) {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  :global(.employer-count) {
    background: #3B82F6 !important;
    color: white !important;
    font-size: 0.75rem !important;
    font-weight: 600 !important;
    padding: 0.125rem 0.375rem !important;
    border-radius: 12px !important;
    flex-shrink: 0 !important;
    line-height: 1 !important;
    border: none !important;
    cursor: pointer !important;
    transition: all 0.15s ease !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    min-width: 20px !important;
    height: 1.25rem !important;
    margin: 0 !important;
  }

  :global(.employer-count:hover) {
    background: #2563EB;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
  }

  :global(.employer-count:active) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(59, 130, 246, 0.3);
  }



  :global(.no-employer) {
    color: #9ca3af;
    font-style: italic;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .filters-section {
      flex-direction: column;
      gap: 1rem;
      align-items: stretch;
    }

    .filters-left {
      flex-direction: column;
      gap: 0.75rem;
    }

    .filter-item {
      min-width: auto;
    }

    .employer-popup {
      position: fixed;
      left: 1rem !important;
      right: 1rem !important;
      top: 50% !important;
      transform: translateY(-50%);
      max-width: none;
    }
  }

</style> 